import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import {
  IS_NATIVE_APP,
  IS_IOS_APP,
  isNativeBlockedPath,
} from "./config/appTarget.js";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Navbar from "./shared/Navbar.js";
import MobileBottomNav, { isBottomNavRoute } from "./shared/MobileBottomNav.js";
import IosLoginSheet from "./auth/IosLoginSheet.js";
import BringtoTop from "./shared/BringtoTop.js";
import Footer from "./shared/Footer.js";
import LandingPage from "./Pages/Landing/LandingPage.js";
import Auth0ProviderWithHistory from "./auth/Auth0Provider.js";
import { WishlistProvider } from "./context/WishlistProvider.js";
import { PawMatchSessionProvider } from "./context/PawMatchSessionProvider.js";
// import { ComparisonProvider } from "./context/ComparisonProvider.js";
import { NotificationsProvider } from "./features/notifications/NotificationsProvider.js";
import { NOTIFICATIONS_ROUTE } from "./features/notifications/routes.js";
import { ForceUpdateGate } from "./features/appUpdate/ForceUpdateGate.js";
import { FeatureGate } from "./features/featureFlags/FeatureGate.js";
import { useFeatureFlag } from "./features/featureFlags/useFeatureFlags.js";
import EmailReachabilityBanner from "./Components/EmailReachabilityBanner.js";
import LoadingPaw from "./Components/LoadingPaw.js";
import { ReactQueryProvider } from "./providers/ReactQueryProvider.js";
import AppGlobalHooks from "./AppGlobalHooks.js";
import OnboardingGuard from "./OnboardingGuard.js";
import AppCrashFallback from "./AppCrashFallback.js";
import AppDownloadFloater from "./shared/AppDownloadFloater.js";
import React, { Suspense, useEffect } from "react";
import UserTypeModalManager from "./Components/UserTypeModalManager.js";
import ScrollToTop from "./shared/ScrollToTop.js";
import NotFound404Page from "./Pages/NotFound404Page.js";
import { useApiWithAuth } from "./hooks/useAuth.js";
import { loginWithReturn } from "./lib/loginWithReturn.js";
import { useUserSync } from "./hooks/useUserSync.js";

const OnboardingCarousel = React.lazy(
  () => import("./Components/onboarding/OnboardingCarousel.js"),
);

function QaAccessGate({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, hasQaAccess, isPermissionsLoading } = useApiWithAuth();
  const { loginWithRedirect } = useAuth0();
  const { t } = useTranslation("auth");
  const isQaEnvironment = import.meta.env.VITE_APP_ENV === "qa";
  // Runs on every authenticated visit, including while QA access is still
  // pending — otherwise a first-time QA login never creates the local User
  // row, and a super admin can never find the email to grant dev:qa:access.
  useUserSync();

  if (!isQaEnvironment) return <>{children}</>;

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-landing-gradient flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
          <h1 className="mb-3 text-2xl font-bold text-my-black">{t("qa.title")}</h1>
          <p className="mb-6 text-sm text-gray-600">
            {t("qa.privateNotice")}
          </p>
          <button
            onClick={() => loginWithReturn(loginWithRedirect)}
            className="w-full rounded-lg bg-main-purple px-6 py-3 font-semibold text-white transition-colors hover:bg-my-black"
          >
            {t("qa.signIn")}
          </button>
        </div>
      </div>
    );
  }

  if (isPermissionsLoading) {
    return (
      <div className="min-h-screen bg-landing-gradient flex items-center justify-center p-4">
        <LoadingPaw size="sm" />
      </div>
    );
  }

  if (!hasQaAccess) {
    return (
      <div className="min-h-screen bg-landing-gradient flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
          <h1 className="mb-3 text-2xl font-bold text-my-black">{t("qa.pendingTitle")}</h1>
          <p className="text-sm text-gray-600">
            {t("qa.pendingNotice")}
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

// Lazy-loaded routes — split out of the main bundle to reduce initial JS size
const KennelList = React.lazy(() => import("./Pages/Kennels/KennelList.js"));
const KennelPage = React.lazy(() => import("./Pages/Kennels/KennelPage.js"));
const KennelMapModal = React.lazy(
  () => import("./features/kennelMap/KennelMapModal.js"),
);
const AnimalInfoPage = React.lazy(
  () => import("./Pages/Animals/AnimalInfoPage.js"),
);
const NinhadaInfoPage = React.lazy(
  () => import("./Pages/Animals/NinhadaInfoPage.js"),
);
// const AnimalComparisonPage = React.lazy(() => import("./Pages/Animals/AnimalComparisonPage.js"));
const DogList = React.lazy(() => import("./Pages/Animals/DogList.js"));
const CatList = React.lazy(() => import("./Pages/Animals/CatList.js"));
const FavoritesPage = React.lazy(
  () => import("./Pages/Animals/FavoritesPage.js"),
);
const TutorialPage = React.lazy(
  () => import("./Pages/Tutorial/TutorialPage.js"),
);
const BeforeAdoption = React.lazy(
  () => import("./Pages/Tutorial/BeforeAdoption.js"),
);
const AdoptionProcess = React.lazy(
  () => import("./Pages/Tutorial/AdoptionProcess.js"),
);
const AfterAdoption = React.lazy(
  () => import("./Pages/Tutorial/AfterAdoption.js"),
);
const WhyAdopt = React.lazy(() => import("./Pages/Information/WhyAdopt.js"));
const BlogLanding = React.lazy(() => import("./Pages/Blog/BlogLanding.js"));
const BlogPostPage = React.lazy(() => import("./Pages/Blog/BlogPost.js"));
const AboutPawseum = React.lazy(
  () => import("./Pages/Information/AboutPawseum.js"),
);
const AdminPage = React.lazy(() =>
  import("./Pages/Admin/AdminPage.js").then((m) => ({ default: m.AdminPage })),
);
const NinhadaEditFlow = React.lazy(
  () => import("./Pages/Admin/Ninhadas/NinhadaEditFlow.js"),
);
const UserSettings = React.lazy(() => import("./Pages/User/UserSettings.js"));
const VerifyEmailPage = React.lazy(() => import("./Pages/User/VerifyEmailPage.js"));
const TiersPage = React.lazy(() => import("./Pages/Tiers/TiersPage.js"));
const NiveisPage = React.lazy(
  () => import("./Pages/PawMatch/NiveisPage.js"),
);
const NotificationsPage = React.lazy(
  () => import("./features/notifications/NotificationsPage.js"),
);
const TermsPage = React.lazy(() => import("./Pages/Privacy/TermsPage.js"));
const PrivacyPage = React.lazy(() => import("./Pages/Privacy/PrivacyPage.js"));
const ChatContentPolicyPage = React.lazy(
  () => import("./Pages/Privacy/ChatContentPolicyPage.js"),
);
const ProfilePage = React.lazy(() => import("./Pages/Profile/ProfilePage.js"));
const AddCatForm = React.lazy(
  () => import("./Pages/Admin/AnimalForm/AddCatFormWrapper.js"),
);
const FormsListPage = React.lazy(
  () => import("./Pages/Admin/FormBuilder/FormsListPage.js"),
);
const AddDogForm = React.lazy(
  () => import("./Pages/Admin/AnimalForm/AddDogFormWrapper.js"),
);
// PawMatch pages are always routable; access is gated (Auth0 + beta permission).
const PawMatch = React.lazy(() => import("./Pages/PawMatch/PawMatchPage.js"));
const PawMatchTutorialPage = React.lazy(
  () => import("./Pages/PawMatch/shared/PawMatchTutorialPage.js"),
);
const PawMatchListPage = React.lazy(
  () => import("./Pages/PawMatch/PawMatchListPage.js"),
);
const AdvertiserApplyPage = React.lazy(
  () => import("./Pages/Advertiser/AdvertiserApplyPage.js"),
);
const AdvertiserStatusPage = React.lazy(
  () => import("./Pages/Advertiser/AdvertiserStatusPage.js"),
);
const AdvertiserDashboard = React.lazy(
  () => import("./Pages/Advertiser/AdvertiserDashboard.js"),
);
const AdvertiserAnimalForm = React.lazy(
  () => import("./Pages/Advertiser/AdvertiserAnimalForm.js"),
);
const AdvertiserSettings = React.lazy(
  () => import("./Pages/Advertiser/AdvertiserSettings.js"),
);
const ParticularProfilePage = React.lazy(
  () => import("./Pages/Advertiser/ParticularProfilePage.js"),
);
const ParticularList = React.lazy(
  () => import("./Pages/Particulares/ParticularList.js"),
);
// --- Agent 1: Kennel join flow ---
const JoinChooserPage = React.lazy(
  () => import("./Pages/Kennel/JoinChooserPage.js"),
);
const MessagesLayout = React.lazy(
  () => import("./Pages/Chat/MessagesLayout.js"),
);
const KennelApplyPage = React.lazy(
  () => import("./Pages/Kennel/KennelApplyPage.js"),
);
const KennelStatusPage = React.lazy(
  () => import("./Pages/Kennel/KennelStatusPage.js"),
);
const PartnershipGuidePage = React.lazy(
  () => import("./Pages/Kennel/PartnershipGuidePage.js"),
)
const MyApplicationsPage = React.lazy(
  () => import("./Pages/Adoption/MyApplicationsPage.js"),
)
const UserApplicationPage = React.lazy(
  () => import("./Pages/Adoption/UserApplicationPage.js"),
)
const KennelApplicationDetailPage = React.lazy(
  () => import("./Pages/Adoption/KennelApplicationDetailPage.js"),
);
const DevToolsPage = React.lazy(() => import("./Pages/Dev/DevToolsPage.js"));
const CoursePlayerPage = React.lazy(() => import("./Pages/Courses/CoursePlayerPage.js"));
const AdoptionFormPage = React.lazy(() => import("./Pages/Adoption/AdoptionFormPage.js"));
const AdvertiserApplicationsPage = React.lazy(
  () => import("./Pages/Advertiser/AdvertiserApplicationsPage.js"),
);
const AdvertiserApplicationDetailPage = React.lazy(
  () => import("./Pages/Advertiser/AdvertiserApplicationDetailPage.js"),
);
const CoursesPreviewPage = import.meta.env.DEV
  ? React.lazy(() => import("./Pages/Dev/CoursesPreviewPage.js"))
  : null;
const ContentReviewCoursePage = React.lazy(
  () => import("./Pages/Admin/ContentReview/ContentReviewCoursePage.js"),
);
const ContentReviewFormPage = React.lazy(
  () => import("./Pages/Admin/ContentReview/ContentReviewFormPage.js"),
);
const ContentReviewBenchmarkPage = React.lazy(
  () => import("./Pages/Admin/ContentReview/ContentReviewBenchmarkPage.js"),
);
import DevToolsFab from "./Components/DevToolsFab.js";

function AppChrome() {
  const location = useLocation();
  const coursesEnabled = useFeatureFlag("courses");

  if (IS_NATIVE_APP && isNativeBlockedPath(location.pathname)) {
    return <Navigate to="/" replace />;
  }
  const isOnboarding = location.pathname === "/onboarding";
  // Course player and adoption form run full-screen: no navbar, footer or bottom nav.
  const isFocusFlow =
    (coursesEnabled &&
      (location.pathname.startsWith("/cursos/") || location.pathname.startsWith("/candidatura/"))) ||
    location.pathname === "/mapa";
  // On iOS the top navbar is always replaced by the bottom tab bar.
  // hideGlobalChrome still suppresses web Navbar + Footer (PawMatch & onboarding).
  // MobileBottomNav is shown everywhere on iOS except onboarding.
  const hideGlobalChrome = location.pathname === "/pawmatch" || isOnboarding || isFocusFlow;
  const hideTopNav = IS_NATIVE_APP || hideGlobalChrome;
  const isPawMatchFullscreen = location.pathname === "/pawmatch";
  // Both PawMatch and onboarding are fullscreen 100dvh views: strip the body's
  // navbar padding-top and lock scroll so they don't overflow (vertical gap /
  // horizontal scroll) on mobile.
  const isFullscreenRoute = isPawMatchFullscreen || isOnboarding;

  useEffect(() => {
    if (isFullscreenRoute) {
      document.body.classList.add("pawmatch-fullscreen");
      return () => {
        document.body.classList.remove("pawmatch-fullscreen");
      };
    }

    document.body.classList.remove("pawmatch-fullscreen");
    return undefined;
  }, [isFullscreenRoute]);

  useEffect(() => {
    document.body.classList.toggle("focus-flow-active", isFocusFlow);
    return () => document.body.classList.remove("focus-flow-active");
  }, [isFocusFlow]);

  const isAdminRoute = location.pathname.startsWith("/painel-admin");
  // overflow:hidden and compact padding only apply to the shell itself, not detail sub-pages
  const isAdminShell = location.pathname === "/painel-admin";
  useEffect(() => {
    document.body.classList.toggle("admin-route", isAdminShell);
    return () => document.body.classList.remove("admin-route");
  }, [isAdminShell]);

  return (
    <>
      {!hideTopNav && <Navbar />}
      {!hideGlobalChrome && <EmailReachabilityBanner />}
      {IS_NATIVE_APP && !isOnboarding && !isFocusFlow && <MobileBottomNav />}
      {IS_IOS_APP && <IosLoginSheet />}
      {!IS_NATIVE_APP &&
        !isFocusFlow &&
        !location.pathname.startsWith("/painel-admin") &&
        !location.pathname.startsWith("/pawmatch") && <AppDownloadFloater />}
      <OnboardingGuard />
      <Suspense
        fallback={
          <div className="h-screen flex items-center justify-center">
            <LoadingPaw size="sm" />
          </div>
        }
      >
        {/* Bottom clearance for the floating nav — must account for the
            home-indicator safe area so nav never overlaps the last content. */}
        <div
          style={
            IS_NATIVE_APP && !isOnboarding && !isFocusFlow && isBottomNavRoute(location.pathname)
              ? { paddingBottom: "calc(env(safe-area-inset-bottom) + 80px)" }
              : undefined
          }
        >
          <Routes>
            {/* Onboarding carousel — native first-run, plus dev web for previewing.
                Not in the navbar; never auto-shown on web. */}
            {(IS_NATIVE_APP || import.meta.env.DEV) && (
              <Route path="/onboarding" element={<OnboardingCarousel />} />
            )}
            {/* PATHS DE ANIMAIS */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/adotar" element={<LandingPage />} />
            <Route path="/canis" element={<KennelList />} />
            <Route path="/mapa" element={<KennelMapModal />} />
            <Route path="/canis/:slug" element={<KennelPage />} />
            <Route
              path="/anunciantes-particulares"
              element={<ParticularList />}
            />
            <Route path="/caes" element={<DogList />} />
            <Route path="/animais" element={<DogList />} />
            <Route
              path="/caes/ninhadas/:id"
              element={
                <FeatureGate flag="ninhadas" fallback={<NotFound404Page />}>
                  <NinhadaInfoPage />
                </FeatureGate>
              }
            />
            <Route
              path="/gatos/ninhadas/:id"
              element={
                <FeatureGate flag="ninhadas" fallback={<NotFound404Page />}>
                  <NinhadaInfoPage />
                </FeatureGate>
              }
            />
            <Route path="/:type/:id" element={<AnimalInfoPage />} />
            <Route path="/cao/:name-:id" element={<AnimalInfoPage />} />
            <Route path="/gato/:name-:id" element={<AnimalInfoPage />} />
            <Route
              path="/caes/adotados"
              element={<DogList status="ADOTADO" />}
            />
            <Route path="/gatos" element={<CatList />} />
            <Route
              path="/gatos/adotados"
              element={<CatList status="ADOTADO" />}
            />
            {/* PATHS DE ADMIN */}
            <Route path="/painel-admin" element={<AdminPage />} />
            <Route path="/feature-flags" element={<Navigate to="/painel-admin?tab=feature-flags" replace />} />
            <Route
              path="/painel-admin/novo-cao"
              element={<AddDogForm mode="create" />}
            />
            <Route
              path="/painel-admin/novo-gato"
              element={<AddCatForm mode="create" />}
            />
            <Route
              path="/painel-admin/editar-cao/:animalName"
              element={<AddDogForm mode="edit" />}
            />
            <Route
              path="/painel-admin/editar-gato/:animalName"
              element={<AddCatForm mode="edit" />}
            />
            <Route
              path="/painel-admin/ninhadas/nova"
              element={
                <FeatureGate flag="ninhadas" fallback={<Navigate to="/painel-admin" replace />}>
                  <NinhadaEditFlow />
                </FeatureGate>
              }
            />
            <Route
              path="/painel-admin/ninhadas/:ninhadaId"
              element={
                <FeatureGate flag="ninhadas" fallback={<Navigate to="/painel-admin" replace />}>
                  <NinhadaEditFlow />
                </FeatureGate>
              }
            />
            <Route
              path="/painel-admin/formularios/:kennelId"
              element={<FormsListPage />}
            />
            {/* PATHS DE FAVORITOS */}
            <Route path="/favoritos" element={<FavoritesPage />} />
            {/* Informative-only routes — web only (hidden on iOS app build) */}
            {!IS_NATIVE_APP && (
              <>
                <Route path="/tutorial-dono" element={<TutorialPage />} />
                <Route
                  path="/tutorial-dono/antes"
                  element={<BeforeAdoption />}
                />
                <Route
                  path="/tutorial-dono/processo"
                  element={<AdoptionProcess />}
                />
                <Route
                  path="/tutorial-dono/depois"
                  element={<AfterAdoption />}
                />
                <Route path="/porque-adotar" element={<WhyAdopt />} />
                <Route path="/blog" element={<BlogLanding />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/sobre-pawseum" element={<AboutPawseum />} />
              </>
            )}
            {/* PATHS DE PAWMATCH */}
            <>
              <Route
                path="/pawmatch"
                element={
                  <Suspense
                    fallback={
                      <div className="h-60vh bg-landing-gradient flex items-center justify-center">
                        <LoadingPaw size="md" />
                      </div>
                    }
                  >
                    <PawMatch />
                  </Suspense>
                }
              />
              <Route
                path="/pawmatch/lista"
                element={
                  <Suspense
                    fallback={
                      <div className="h-60vh bg-landing-gradient flex items-center justify-center">
                        <LoadingPaw size="sm" />
                      </div>
                    }
                  >
                    <PawMatchListPage />
                  </Suspense>
                }
              />
              <Route
                path="/pawmatch/tutorial"
                element={
                  <Suspense
                    fallback={
                      <div className="h-60vh bg-landing-gradient flex items-center justify-center">
                        <LoadingPaw size="sm" />
                      </div>
                    }
                  >
                    <PawMatchTutorialPage />
                  </Suspense>
                }
              />
            </>
            {/* PATHS DE PRIVACIDADE E DEFINIÇÕES */}
            <Route path="/definicoes" element={<UserSettings />} />
            <Route path="/verificar-email" element={<VerifyEmailPage />} />
            <Route path={NOTIFICATIONS_ROUTE} element={<NotificationsPage />} />
            <Route path="/candidaturas" element={<FeatureGate flag="admin_pipeline" fallback={<NotFound404Page />}><MyApplicationsPage /></FeatureGate>} />
            <Route path="/candidaturas/:id" element={<FeatureGate flag="admin_pipeline" fallback={<NotFound404Page />}><UserApplicationPage /></FeatureGate>} />
            <Route path="/painel-admin/candidaturas/:id" element={<FeatureGate flag="admin_pipeline" fallback={<NotFound404Page />}><KennelApplicationDetailPage /></FeatureGate>} />
            <Route path="/painel-admin/revisao-conteudo/cursos/:courseKey" element={<FeatureGate flag="courses" fallback={<NotFound404Page />}><ContentReviewCoursePage /></FeatureGate>} />
            <Route path="/painel-admin/revisao-conteudo/formularios/:systemKey" element={<FeatureGate flag="courses" fallback={<NotFound404Page />}><ContentReviewFormPage /></FeatureGate>} />
            <Route path="/painel-admin/revisao-conteudo/benchmark" element={<FeatureGate flag="courses" fallback={<NotFound404Page />}><ContentReviewBenchmarkPage /></FeatureGate>} />
            <Route path="/mensagens" element={<FeatureGate flag="admin_pipeline" fallback={<NotFound404Page />}><MessagesLayout /></FeatureGate>} />
            <Route path="/mensagens/:id" element={<FeatureGate flag="admin_pipeline" fallback={<NotFound404Page />}><MessagesLayout /></FeatureGate>} />
            <Route path="/dev-tools" element={<DevToolsPage />} />
            {CoursesPreviewPage && (
              <Route path="/dev/cursos-preview/:tab?" element={<CoursesPreviewPage />} />
            )}
            <Route
              path="/cursos/:courseKey"
              element={<FeatureGate flag="courses" fallback={<NotFound404Page />}><CoursePlayerPage /></FeatureGate>}
            />
            <Route
              path="/candidatura/:animalId"
              element={<FeatureGate flag="courses" fallback={<NotFound404Page />}><AdoptionFormPage /></FeatureGate>}
            />
            <Route path="/pawmatch/niveis" element={<NiveisPage />} />
            <Route path="/planos" element={<FeatureGate flag="paid_tiers" fallback={<NotFound404Page />}><TiersPage /></FeatureGate>} />
            <Route path="/termos" element={<TermsPage />} />
            <Route path="/privacidade" element={<PrivacyPage />} />
            <Route path="/politica-chat" element={<ChatContentPolicyPage />} />
            {/* "Sobre mim" — user dashboard (likes, matches, applications).
                Reachable on web (desktop dropdown) and native (bottom nav). */}
            <Route path="/perfil" element={<ProfilePage />} />
            {/* PATHS DE ANUNCIANTE PARTICULAR */}
            {/* --- Agent 1: Kennel join flow --- */}
            {/* /quero-anunciar is now the type chooser (Particular vs Canil/Abrigo) */}
            <Route path="/quero-anunciar" element={<JoinChooserPage />} />
            <Route
              path="/quero-anunciar/particular"
              element={<AdvertiserApplyPage />}
            />
            <Route path="/quero-anunciar/canil" element={<KennelApplyPage />} />
            <Route
              path="/candidatura-canil/estado"
              element={<KennelStatusPage />}
            />
            <Route
              path="/quero-anunciar/parceria"
              element={<PartnershipGuidePage />}
            />
            <Route
              path="/meu-anuncio/status"
              element={<AdvertiserStatusPage />}
            />
            <Route path="/meu-anuncio" element={<AdvertiserDashboard />} />
            <Route
              path="/meu-anuncio/adicionar"
              element={<AdvertiserAnimalForm />}
            />
            <Route
              path="/meu-anuncio/definicoes"
              element={<AdvertiserSettings />}
            />
            <Route
              path="/meu-anuncio/candidaturas"
              element={<FeatureGate flag="courses" fallback={<NotFound404Page />}><AdvertiserApplicationsPage /></FeatureGate>}
            />
            <Route
              path="/meu-anuncio/candidaturas/:id"
              element={<FeatureGate flag="courses" fallback={<NotFound404Page />}><AdvertiserApplicationDetailPage /></FeatureGate>}
            />
            <Route
              path="/particular/:slug"
              element={<ParticularProfilePage />}
            />
            {/* 404 - CATCH ALL ROUTE (MUST BE LAST) */}
            <Route path="*" element={<NotFound404Page />} />
          </Routes>
        </div>
      </Suspense>
      {!hideGlobalChrome && !IS_NATIVE_APP && location.pathname !== "/" && !location.pathname.startsWith("/painel-admin") && (
        <BringtoTop />
      )}
      {!hideGlobalChrome && !IS_NATIVE_APP && !location.pathname.startsWith("/painel-admin") && <Footer />}
      <DevToolsFab />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ReactQueryProvider>
        <Auth0ProviderWithHistory>
          <WishlistProvider>
            <PawMatchSessionProvider>
              {/* COMMENTED OUT - Comparison functionality temporarily disabled */}
              {/* <ComparisonProvider> */}
              <AppGlobalHooks />
              <UserTypeModalManager />
              <ScrollToTop />
              <NotificationsProvider>
                <Sentry.ErrorBoundary fallback={<AppCrashFallback />}>
                  <ForceUpdateGate>
                    <QaAccessGate>
                      <AppChrome />
                    </QaAccessGate>
                  </ForceUpdateGate>
                </Sentry.ErrorBoundary>
              </NotificationsProvider>

              {/* Toast Container */}
              <ToastContainer
                limit={2}
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss={false}
                draggable
                pauseOnHover={false}
                theme="light"
              />
              {/* </ComparisonProvider> */}
            </PawMatchSessionProvider>
          </WishlistProvider>
        </Auth0ProviderWithHistory>
      </ReactQueryProvider>
    </BrowserRouter>
  );
}

export default App;
